/**
 * Whether a vehicle assignment is in force on a given date (M1-27).
 *
 * <b>The client half of `VehicleAssignmentCalendar.AppliesOn`</b>, and
 * deliberately the same shape: both ends inclusive, a null `effectiveTo`
 * meaning open-ended. The server owns the predicate everywhere a DATE is part
 * of the question — the dispatch board picks the day it is drawing and the API
 * answers for that day, which is the arrangement `lib/availability.ts`
 * describes and the reason the board and its tooltip cannot disagree.
 *
 * ⚠️ The profile page has no such date. It asks "what is this person driving
 * NOW", and "now" is a fact about a clock rather than about a request, so
 * there is nothing to hand the server. The one clock this app answers that
 * question on is the ACCOUNT's — `todayInZone`, which is exactly how
 * `Dispatch.tsx` chooses the board's default date — so using it here is
 * reusing that clock rather than introducing a second one.
 *
 * The bug this exists to fix: the profile section called an assignment current
 * iff `effectiveTo === null`. A technician lent Van 3 from 2026-09-01 to
 * 2026-12-31 — a dated stint, the ordinary shape of a seasonal loan — was
 * therefore rendered as history, so when Van 3 went into the shop the board's
 * tooltip warned and the profile page said nothing. Two surfaces disagreeing
 * about one Tuesday, which is the M1-39 failure this story cites as its reason
 * for owning the predicate server-side in the first place.
 */
import { toIsoDate, type CivilDate } from './timezone';

/** The two dates an assignment is bounded by. `effectiveTo` null means open-ended. */
export interface AssignmentWindow {
  effectiveFrom: string;
  effectiveTo?: string | null;
}

/**
 * Both ends inclusive: a one-day assignment is a real assignment, and a stint
 * that ends today has not ended yet.
 *
 * ISO `YYYY-MM-DD` strings compare lexicographically in date order, so this
 * needs no parsing — and parsing is where a date-only string would pick up a
 * timezone it does not have.
 */
export function assignmentAppliesOn(assignment: AssignmentWindow, today: CivilDate): boolean {
  const date = toIsoDate(today);
  return (
    assignment.effectiveFrom <= date &&
    (assignment.effectiveTo == null || assignment.effectiveTo >= date)
  );
}
